// src/pages/AboutYou.tsx
// Step 3 of 5 - Consolidated: BasicInfo + ActivityLevel
// Styled to match app dashboard

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase, currentUser } from "@/lib/supabase";
import { markCompleted } from "@/lib/onboarding";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const ACTIVITY_LEVELS = [
  { value: "sedentary", label: "Sedentary", desc: "Little or no exercise" },
  { value: "lightly active", label: "Light", desc: "1-2 days/week" },
  { value: "moderately active", label: "Moderate", desc: "3-5 days/week" },
  { value: "very active", label: "Active", desc: "6-7 days/week" },
  { value: "extremely active", label: "Athlete", desc: "2x daily" },
];

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-500 ${
            i < current 
              ? "w-8 bg-[#a78bfa]" 
              : i === current 
                ? "w-8 bg-[#a78bfa]/40" 
                : "w-3 bg-[#f1f5f9]"
          }`}
        />
      ))}
    </div>
  );
}

export default function AboutYou() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    dob: "",
    phone: "+44",
    height: "",
    heightUnit: "cm",
    weight: "",
    gender: "",
    activityLevel: "",
  });

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const calculateAge = (dob: string) => {
    const today = new Date();
    const birth = new Date(dob);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const isFormValid = () => {
    if (!form.name || !form.dob || !form.gender || !form.height || !form.weight || !form.activityLevel) {
      return false;
    }
    if (calculateAge(form.dob) < 16) return false;
    if (form.phone.length < 13 || !form.phone.startsWith("+44")) return false;
    return true;
  };

  const handleNext = async () => {
    setErr(null);
    setSaving(true);

    try {
      const user = await currentUser();
      if (!user) throw new Error("Not logged in");

      const age = calculateAge(form.dob);
      if (age < 16) {
        setErr("You must be at least 16 years old.");
        setSaving(false);
        return;
      }

      const heightCm = form.heightUnit === "in" 
        ? Number(form.height) * 2.54 
        : Number(form.height);

      const supabase = getSupabase();

      const { error } = await supabase.from("clients").upsert(
        {
          user_id: user.id,
          name: form.name.trim(),
          date_of_birth: form.dob,
          age,
          phone: form.phone,
          height_in_cm: heightCm || null,
          weight_in_kg: Number(form.weight) || null,
          gender: form.gender.toLowerCase(),
          activity_level: form.activityLevel,
          email_address: user.email,
        },
        { onConflict: "user_id" }
      );

      if (error) throw error;

      markCompleted("aboutYou");
      navigate("/create-goal");
    } catch (e: any) {
      setErr(e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // Max DOB for 16+ years old
  const maxDob = new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center px-4 py-6">
        <div className="w-full max-w-md">
          
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              About You
            </h1>
            <p className="text-gray-500 text-sm">
              We'll use this to personalise your plan
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5">
            <div className="space-y-4">
              
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Name</label>
                <input
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                />
              </div>

              {/* DOB + Gender */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    value={form.dob}
                    onChange={(e) => setForm(f => ({ ...f, dob: e.target.value }))}
                    max={maxDob}
                    className="w-full h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] focus:outline-none focus:border-[#a78bfa]/50 transition-all [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Gender</label>
                  <div className="grid grid-cols-2 gap-2">
                    {["Male", "Female"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, gender: g }))}
                        className={`h-12 rounded-xl font-medium text-sm transition-all duration-200 ${
                          form.gender === g
                            ? "bg-[#a78bfa] text-[#0f172a]"
                            : "bg-[#f1f5f9] text-gray-500 border border-[#e2e8f0] hover:border-[#e2e8f0]"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Height + Weight */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Height</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="180"
                      value={form.height}
                      onChange={(e) => setForm(f => ({ ...f, height: e.target.value }))}
                      className="flex-1 h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                    />
                    <select
                      value={form.heightUnit}
                      onChange={(e) => setForm(f => ({ ...f, heightUnit: e.target.value }))}
                      className="w-16 h-12 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl px-2 text-[#0f172a] text-sm focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                    >
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Weight (kg)</label>
                  <input
                    type="number"
                    placeholder="75"
                    value={form.weight}
                    onChange={(e) => setForm(f => ({ ...f, weight: e.target.value }))}
                    className="w-full h-12 px-3 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 transition-all"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">
                  Phone Number
                  <span className="text-gray-600 font-normal ml-1">(UK)</span>
                </label>
                <PhoneInput
                  country="gb"
                  onlyCountries={["gb"]}
                  preferredCountries={["gb"]}
                  value={form.phone}
                  onChange={(phone) => {
                    let cleaned = phone || "";
                    if (cleaned.startsWith("440")) cleaned = cleaned.substring(2);
                    cleaned = cleaned.replace(/^0+/, "");
                    if (!cleaned.startsWith("44")) cleaned = "44" + cleaned;
                    setForm(f => ({ ...f, phone: "+" + cleaned }));
                  }}
                  placeholder="7123 456 789"
                  inputProps={{ required: true }}
                  containerClass="w-full"
                  inputClass="!w-full !h-12 !bg-[#f1f5f9] !border !border-[#e2e8f0] !rounded-xl !text-[#0f172a] !text-base !pl-14 focus:!outline-none focus:!border-[#a78bfa]/50 !transition-all"
                  buttonClass="!bg-[#f1f5f9] !border !border-[#e2e8f0] !rounded-l-xl !h-12 hover:!bg-[#f1f5f9]"
                  dropdownClass="!bg-[#f1f5f9] !border-[#e2e8f0] !rounded-xl"
                />
                <p className="text-xs text-gray-600 mt-2">
                  We'll verify this via WhatsApp
                </p>
              </div>

              {/* Activity Level */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-3">Activity Level</label>
                <div className="grid grid-cols-5 gap-2">
                  {ACTIVITY_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, activityLevel: level.value }))}
                      className={`p-2 rounded-xl text-center transition-all duration-200 ${
                        form.activityLevel === level.value
                          ? "bg-[#a78bfa] text-[#0f172a]"
                          : "bg-[#f1f5f9] text-gray-500 border border-[#e2e8f0] hover:border-[#e2e8f0]"
                      }`}
                    >
                      <p className="font-medium text-xs">{level.label}</p>
                      <p className={`text-[8px] mt-0.5 leading-tight ${
                        form.activityLevel === level.value ? "text-[#0f172a]/60" : "text-gray-600"
                      }`}>
                        {level.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={handleNext}
              disabled={saving || !isFormValid()}
              className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
            >
              {saving ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : "Continue"}
            </button>

            {/* Error */}
            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}
          </div>

          {/* Step Indicator */}
          <div className="mt-8">
            <StepIndicator current={2} total={5} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>

      {/* Custom styles for phone input */}
      <style>{`
        .react-tel-input .flag-dropdown {
          background: transparent !important;
        }
        .react-tel-input .selected-flag:hover,
        .react-tel-input .selected-flag:focus {
          background: #f1f5f9 !important;
        }
        .react-tel-input .country-list {
          background: #f1f5f9 !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 12px !important;
        }
        .react-tel-input .country-list .country:hover {
          background: #f1f5f9 !important;
        }
        .react-tel-input .country-list .country.highlight {
          background: rgba(167,139,250,0.2) !important;
        }
        .react-tel-input .country-list .country-name,
        .react-tel-input .country-list .dial-code {
          color: #0f172a !important;
        }
      `}</style>
    </div>
  );
}
